import { useCampaignDetail } from 'data/campaign';
import {
  Field,
  Form, Formik,
} from 'formik';
import _ from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ECampaignStatus } from 'types/campaign';
import * as Yup from 'yup';

import { Box, ButtonBase } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';

import Button from 'creative-components/CustomButtons/Button';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import ActionModal from 'components/ActionModal/ActionModal';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import CampaignCancelModal from 'components/CampaignSettings/CampaignCancelModal';
import ChooseAgentLandingPageType from 'components/ChooseAgentLandingPageType/ChooseAgentLandingPageType';
import ChooseDeliveryAddressTarget from 'components/ChooseDeliveryAddressTarget';
import ChooseTouchTemplateDesign from 'components/ChooseTouchTemplateDesign/ChooseTouchTemplateDesign';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import { ESaveButtonFormikStatus } from 'components/SaveButton/SaveButton';

import {
  cancelCampaign, createAutoRenewalCampaign,
  getCampaignCancellationFee, pauseCampaign, updateCampaign,
} from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: '60px',
    '& > div:first-child': {
      margin: '20px 0 0',
    },
    '& > div:not(:first-child)': {
      margin: '40px 0 0',
    },
    '& > div > span:first-child': {
      fontWeight: 'bold',
      fontSize: '16px',
      lineHeight: '24px',
      color: theme.palette.grayScale9.main,
    },
    '& > div > span:not(:first-child)': {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      color: theme.palette.lightGray.main,
      margin: '8px 0 20px',
    },
  },
  settingContainer: {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    padding: '20px',
    margin: '0 0 30px',

    '& > h4': {
      fontSize: '16px',
      fontWeight: 600,
      color: theme.palette.darkGray.main,
    },
    '& > h5': {
      fontSize: '14px',
      color: theme.palette.gray.main,
      fontWeight: 500,
      margin: '6px 0 10px',
    },
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
  settingInput: {
    maxWidth: '400px',
    fontWeight: 500,
  },
  saveButtonContainer: {
    float: 'right',
  },
}));

const CampaignSettings = ({ campaignId }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();
  const { isAdmin, isBrokerageAdmin, isAnyAgent } = useAuthDataContext();

  const { campaign, isLoadingCampaign, mutateCampaign } = useCampaignDetail(campaignId);

  const [showConfirmation, setShowConfirmation] = React.useState(null);

  const onPauseCampaign = async () => {
    showLoadingIndicatorModal();

    try {
      await pauseCampaign(campaign._id);
      toast.success(`${campaign.agent.firstName} ${campaign.agent.lastName}'s ${campaign.eventType.name} campaign has been paused.`);

      // Update the campaign
      mutateCampaign();

      setShowConfirmation(null);
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      setShowConfirmation(null);
      hideLoadingIndicatorModal();
    }
  };

  const onCancelCampaign = async (waiveCancellationFee) => {
    showLoadingIndicatorModal();

    try {
      await cancelCampaign(campaign._id, waiveCancellationFee);

      // Reload the page
      window.location.reload();

      toast.success(`${campaign.agent.firstName} ${campaign.agent.lastName}'s ${campaign.eventType.name} campaign has been cancelled.`);
    } catch (err) {
      if (err.response && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Something went wrong, please try again.');
      }

      console.error('error', err);
    } finally {
      setShowConfirmation(null);
      hideLoadingIndicatorModal();
    }
  };

  const onCreateAutoRenewalCampaign = async () => {
    showLoadingIndicatorModal();

    try {
      const { autoRenewalCampaign, autoRenewalCampaignAlreadyExisted, autoRenewalCampaignDeleted } = await createAutoRenewalCampaign(campaign._id);

      const campaignLink = (
        <Link to={`/dashboard/campaigns/${autoRenewalCampaign._id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
          Go to Auto-Renewal Campaign
        </Link>
      );

      if (autoRenewalCampaignAlreadyExisted) {
        if (autoRenewalCampaignDeleted) {
          toast.warning('This campaign was already set to auto-renew, but the renewal was cancelled');
        } else {
          toast.warning((
            <div style={{ fontWeight: 600 }}>
              This campaign is already scheduled to auto-renew. You may check the details here:
              <br />
              {campaignLink}
            </div>
          ));
        }
      } else {
        toast.success((
          <div style={{ fontWeight: 600 }}>
            An auto-renewal campaign has been created. You may check the details here:
            <br />
            {campaignLink}
          </div>
        ));
      }
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      setShowConfirmation(null);
      hideLoadingIndicatorModal();
    }
  };

  const onClickPauseCampaign = () => {
    setShowConfirmation(<ActionModal
      backgroundColor={theme.palette.offWhite.main}
      textColor={theme.palette.darkGray.main}
      icon={<LibraryAddCheckRoundedIcon />}
      onClose={() => setShowConfirmation(null)}
      title="Are You Sure?"
      message="Are you sure you want to pause this campaign?"
      buttons={[
        <ButtonBase onClick={() => setShowConfirmation(null)} className={classes.cancelButton}>No</ButtonBase>,
        <ButtonBase onClick={onPauseCampaign}>Yes</ButtonBase>,
      ]}
    />);
  };

  const onClickCancelCampaign = async () => {
    showLoadingIndicatorModal();

    try {
      let cancellationFee;

      // Fetch how much it will cost the agent to cancel this campaign for monthly billed campaigns
      if (!campaign.costs.isAnnualBilling) {
        const data = await getCampaignCancellationFee(campaign._id);
        cancellationFee = data.cancellationFee;
      } else {
        cancellationFee = 0;
      }

      setShowConfirmation(<CampaignCancelModal
        campaign={campaign}
        cancellationFee={cancellationFee}
        onConfirm={onCancelCampaign}
        onClose={() => setShowConfirmation(null)}
      />);
    } catch (error) {
      if (error.response && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Something went wrong, please try again.');
      }

      console.error('error', error);
    }

    hideLoadingIndicatorModal();
  };

  const onClickCreateAutoRenewalCampaign = async () => {
    setShowConfirmation(<ActionModal
      backgroundColor={theme.palette.offWhite.main}
      textColor={theme.palette.darkGray.main}
      icon={<LibraryAddCheckRoundedIcon />}
      onClose={() => setShowConfirmation(null)}
      title="Auto-Renew This Campaign?"
      message="Are you sure you want to renew this campaign which, unless cancelled by the user, will automatically start in 20 days?"
      buttons={[
        <ButtonBase onClick={() => setShowConfirmation(null)} className={classes.cancelButton}>No</ButtonBase>,
        <ButtonBase onClick={onCreateAutoRenewalCampaign}>Yes</ButtonBase>,
      ]}
    />);
  };

  if (isLoadingCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  const isCampaignAdmin = isAdmin || isBrokerageAdmin;
  const canPauseCampaign = campaign.status === ECampaignStatus.Active && isCampaignAdmin;
  const canCancelCampaign = isCampaignAdmin && ([ECampaignStatus.Active, ECampaignStatus.Paused].includes(campaign.status));

  return (
    <div className={classes.root}>
      {showConfirmation}

      {isAnyAgent && campaign.status !== 'COMPLETED' && (
        <Formik
          initialValues={{
            farmAreaName: campaign.farmAreaName,
            agentLandingPageType: campaign.agentLandingPageType,
            deliveryAddressTarget: campaign.deliveryAddressTarget,
            touchTemplateDesignId: campaign.touchTemplateDesign,
          }}
          onSubmit={async (values, { resetForm, setStatus }) => {
            const {
              farmAreaName, agentLandingPageType, deliveryAddressTarget, touchTemplateDesignId,
            } = values;

            try {
              await updateCampaign(campaignId, {
                farmAreaName, agentLandingPageType, deliveryAddressTarget, touchTemplateDesignId,
              });

              mutateCampaign({
                campaign: {
                  ...campaign,
                  farmAreaName,
                  agentLandingPageType,
                  deliveryAddressTarget,
                },
              }, { revalidate: true });

              toast.success('Your changes have been saved.');

              // Call resetForm to set the dirty var to false (used for SaveButton to detect changes again). Also
              // pass in the current values here to use that as the new initial state when resetting.
              resetForm({ values });
            } catch (err) {
              setStatus(ESaveButtonFormikStatus.Error);
              console.error('error', err);
              showAPIErrorAlert(err);
            }
          }}
          validationSchema={Yup.object().shape({
            farmAreaName: Yup.string().required(),
            agentLandingPageType: Yup.string().required(),
            deliveryAddressTarget: Yup.string().required(),
            touchTemplateDesignId: Yup.string().required(),
          })}
        >
          {({
            values,
            errors,
            submitForm,
          }) => {
            const previousValues = React.useRef(values);

            // Save settings a second after every value change
            const debouncedSubmit = React.useMemo(
              () => _.debounce(() => submitForm(), 1000),
              [submitForm],
            );

            React.useEffect(() => {
              const hasChanged = Object.keys(values).some(
                (key) => values[key] !== previousValues.current[key],
              );

              if (!hasChanged) return null;

              // Use debounce for text inputs
              if (values.farmAreaName !== previousValues.current.farmAreaName) {
                debouncedSubmit();
              } else {
                submitForm();
              }

              previousValues.current = values;

              return () => {
                debouncedSubmit.cancel();
              };
            }, [values, submitForm, debouncedSubmit]);

            return (
              <Form>
                <GridContainer justifyContent="flex-start">
                  <GridItem xs={12}>
                    <div className={classes.settingContainer}>
                      <h4>General</h4>
                      <h5>Manage your basic preferences and default configurations.</h5>

                      <Box style={{ height: '40px' }} />

                      <Field name="farmAreaName">
                        {({ field }) => (
                          <CustomInputDashboard
                            labelText="Farm Area Name"
                            inputProps={{
                              ...field,
                              placeholder: 'Enter name',
                              endAdornment: <InfoOutlinedIcon />,
                              className: classes.settingInput,
                            }}
                            error={errors[field.name] !== undefined}
                            tooltip="This will be your farm area name merged onto certain marketing materials and refers to you being an expert in that community, neighborhood, zipcode and/or city, i.e. Hollywood Hills, The Westside, Crescent Heights"
                          />
                        )}
                      </Field>

                      <Box style={{ height: '40px' }} />

                      {/* Only show the agent landing page selector for non-commercial properties since we don't do comparables for commercial properties */}
                      {campaign.eventType.name !== 'Commercial' && (
                        <ChooseAgentLandingPageType />
                      )}

                      <Box style={{ height: '40px' }} />

                      <ChooseDeliveryAddressTarget />

                      <Box style={{ height: '40px' }} />

                    </div>
                  </GridItem>

                  <GridItem xs={12}>
                    <div className={classes.settingContainer}>
                      <ChooseTouchTemplateDesign eventType={campaign.eventType} campaignId={campaign._id} />
                    </div>
                  </GridItem>
                </GridContainer>
              </Form>
            );
          }}
        </Formik>
      )}

      {(canPauseCampaign || canCancelCampaign) && (
        <div>
          <span>{`${canPauseCampaign ? 'Pause' : 'Cancel'} Campaign`}</span>
          <br />
          <br />
          <span>
            {canPauseCampaign
              ? 'Pause this campaign to stop billing and marketing touches. It can be resumed later from the paused campaign banner.'
              : 'Cancel this paused campaign and prevent any future marketing touches from being sent.'}
          </span>
          <br />
          <br />
          <div style={{
            display: 'flex', direction: 'row', gap: '20px', flexWrap: 'wrap',
          }}
          >
            {canPauseCampaign && (
              <div>
                <Button
                  color="primary"
                  round
                  onClick={onClickPauseCampaign}
                >
                  Pause Campaign
                </Button>
              </div>
            )}
            {canCancelCampaign && (
              <div>
                <Button
                  onClick={onClickCancelCampaign}
                  color="orange"
                  round
                >
                  Cancel Campaign
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {(isAdmin || isBrokerageAdmin) && [ECampaignStatus.Completed, ECampaignStatus.Cancelled].includes(campaign.status) && (
        <div>
          <span>
            Auto-Renew Campaign
          </span>
          <br />
          <br />
          <span>
            Creates a new draft campaign that will automatically start once this current campaign ends.
            The farm area will be unchanged and the same APNs will be in the new campaign.
            The user may change campaign settings like edit touches, add extra recipients, and change marketing designs
            before the campaign starts. The draft campaign may also be discarded at any time before it starts.
          </span>
          <br />
          <br />
          <div style={{
            display: 'flex', direction: 'row', gap: '20px', flexWrap: 'wrap',
          }}
          >
            <div>
              <Button
                color="primary"
                round
                onClick={onClickCreateAutoRenewalCampaign}
              >
                Auto-Renew Campaign
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignSettings;
